/**
 * Presentation helpers for money, dates and enum-like strings.
 * Pure functions only — safe to use anywhere and easy to unit test.
 */

/**
 * Format a money value for display.
 *
 * The GraphQL API returns money in two shapes:
 *   1. An object `{ amount, currency, formatted }` (preferred — backend formats it)
 *   2. A raw number (e.g. `order.totalAmount`)
 *
 * We always prefer the server-provided `formatted` string when present.
 *
 * @param {object|number|null|undefined} value
 * @param {{ currency?: string }} [options]
 * @returns {string}
 */
export function formatMoney(value, { currency = 'INR' } = {}) {
  if (value == null) return '—';

  if (typeof value === 'object') {
    if (value.formatted) return value.formatted;
    if (value.amount != null) {
      return formatCurrency(value.amount, value.currency || currency);
    }
    return '—';
  }

  return formatCurrency(value, currency);
}

/** Format a numeric amount with an ISO currency code. */
export function formatCurrency(amount, currency = 'INR') {
  const number = Number(amount);
  if (Number.isNaN(number)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(number);
  } catch {
    return `${currency} ${number.toFixed(2)}`;
  }
}

/** Format an ISO date string as e.g. "12 Jun 2026". */
export function formatDate(value) {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Format an ISO date string as e.g. "12 Jun 2026, 3:45 PM". */
export function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** Relative time such as "3h ago" / "2d ago". Falls back to a date. */
export function formatRelativeTime(value) {
  const date = toDate(value);
  if (!date) return '—';
  const diffMs = date.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const abs = Math.abs(diffMin);

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60) return rtf.format(diffMin, 'minute');
  if (abs < 60 * 24) return rtf.format(Math.round(diffMin / 60), 'hour');
  if (abs < 60 * 24 * 7) return rtf.format(Math.round(diffMin / (60 * 24)), 'day');
  return formatDate(value);
}

/**
 * Turn a SCREAMING_SNAKE_CASE enum into a readable label.
 * e.g. "READY_FOR_PICKUP" -> "Ready for pickup".
 */
export function humanizeEnum(value) {
  if (!value) return '';
  const text = String(value).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Best-effort conversion of an unknown value to a valid Date, or null. */
function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
